import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  ChartBarIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { getScoreColors } from '../utils/colorUtils';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface DemoData {
  organization: {
    id: string;
    name: string;
    industry: string;
  };
  projects: Array<{
    id: string;
    name: string;
    description: string;
    status: string;
  }>;
  problems: Array<{
    id: string;
    title: string;
    description: string;
    impact_area: string;
    priority: number;
  }>;
  hypotheses: Array<{
    id: string;
    title: string;
    hypothesis_statement: string;
    priority_score: number;
    business_impact: number;
    technical_feasibility: number;
    implementation_effort: number;
  }>;
}

const DemoViewerPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DemoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDemoData = async () => {
      try {
        const response = await fetch('/public/demo');
        if (!response.ok) {
          throw new Error('Failed to load demo data');
        }
        const demoData = await response.json();
        setData(demoData);
      } catch (err) {
        console.error('Error fetching demo:', err);
        setError('Failed to load demo organization data');
      } finally {
        setLoading(false);
      }
    };

    fetchDemoData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading demo...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const topProblems = [...data.problems]
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 3);

  const topHypotheses = [...data.hypotheses]
    .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/login')}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Login
            </button>
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center px-3 py-1.5 border border-yellow-200 bg-yellow-50 rounded-md text-sm text-yellow-800">
                <EyeIcon className="h-4 w-4 mr-2" />
                Demo View (Read-Only)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Organization Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {data.organization.name}
          </h1>
          <p className="text-lg text-gray-600">
            {data.organization.industry} • Demo Organization
          </p>
          <p className="mt-4 text-sm text-gray-500 max-w-2xl mx-auto">
            This is a read-only demo showcasing the Lean AI methodology.
            <a href="/login" className="text-indigo-600 hover:text-indigo-700 ml-1">
              Sign up to create your own organization →
            </a>
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Projects</p>
                <p className="text-3xl font-bold text-gray-900">{data.projects.length}</p>
              </div>
              <ChartBarIcon className="h-12 w-12 text-indigo-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Business Problems</p>
                <p className="text-3xl font-bold text-gray-900">{data.problems.length}</p>
              </div>
              <ExclamationTriangleIcon className="h-12 w-12 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">AI Hypotheses</p>
                <p className="text-3xl font-bold text-gray-900">{data.hypotheses.length}</p>
              </div>
              <LightBulbIcon className="h-12 w-12 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Top Problems */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 mr-2" />
              Top Business Problems
            </h2>
            <div className="space-y-4">
              {topProblems.map((problem) => (
                <div key={problem.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{problem.title}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColors(problem.priority).background} ${getScoreColors(problem.priority).text}`}>
                      Priority: {problem.priority}/10
                    </span>
                  </div>
                  <MarkdownRenderer content={problem.description} className="text-sm text-gray-600 line-clamp-2" />
                  <div className="mt-2">
                    <span className="text-xs text-gray-500">Impact: {problem.impact_area}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Hypotheses */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <LightBulbIcon className="h-6 w-6 text-yellow-500 mr-2" />
              Top AI Hypotheses
            </h2>
            <div className="space-y-4">
              {topHypotheses.map((hypothesis) => (
                <div key={hypothesis.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{hypothesis.title}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`}>
                      Score: {hypothesis.priority_score?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 italic mb-3">
                    {hypothesis.hypothesis_statement}
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className={`font-bold ${getScoreColors(hypothesis.business_impact).textOnly}`}>
                        {hypothesis.business_impact}/10
                      </div>
                      <div className="text-xs text-gray-500">Impact</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-bold ${getScoreColors(hypothesis.technical_feasibility).textOnly}`}>
                        {hypothesis.technical_feasibility}/10
                      </div>
                      <div className="text-xs text-gray-500">Feasibility</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-bold ${getScoreColors(hypothesis.implementation_effort, true).textOnly}`}>
                        {hypothesis.implementation_effort}/10
                      </div>
                      <div className="text-xs text-gray-500">Effort</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Ready to Build Your Own?</h2>
          <p className="text-indigo-100 mb-6">
            Start managing your AI value hypotheses with the Lean AI methodology
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-white text-indigo-600 rounded-md font-medium hover:bg-gray-100 transition-colors"
          >
            Sign Up Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoViewerPage;
